import { useRef, useEffect, useState } from 'react';

const PHRASES = [
  '... what needs to move today',
  '... thinking begins here',
  '... start anywhere',
  '... ready when you are',
  '... say it out loud first',
  '... what are you carrying today',
  '... the work continues',
  '... no agenda. just thinking.',
];

export default function HomeScreen({
  userRecord,
  onSend,
  input,
  setInput,
  voiceEnabled,
  voiceListening,
  toggleVoice,
  onVoiceToggle,
  onDeepSearch,
  deepSearchActive,
  onFileUpload,
  pendingFile,
  onClearFile,
}) {
  const inputRef = useRef(null);
  const [phrase] = useState(() => PHRASES[Math.floor(Math.random() * PHRASES.length)]);

  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, []);

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      const text = inputRef.current?.innerText?.trim();
      if (text || pendingFile) onSend(text || '');
    }
  }

  const aiName = userRecord?.ai_name || 'shift';

  return (
    <div style={{
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      paddingBottom: '10%',
    }}>
      <div style={{
        width: '100%',
        maxWidth: 600,
        padding: '0 24px',
        boxSizing: 'border-box',
      }}>
        {/* AI name */}
        <div style={{
          textAlign: 'center',
          fontSize: 13,
          letterSpacing: '0.2em',
          color: '#c8a96e',
          fontWeight: 'bold',
          textTransform: 'uppercase',
          marginBottom: 8,
          fontFamily: 'Georgia, serif',
        }}>
          {aiName}
        </div>

        {/* Rotating phrase */}
        <div style={{
          textAlign: 'center',
          color: '#7a9e82',
          fontSize: 12,
          fontStyle: 'italic',
          fontFamily: 'Georgia, serif',
          marginBottom: 32,
        }}>
          {phrase}
        </div>

        {/* Pending file badge */}
        {pendingFile && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', background: '#111', border: '1px solid #272736', borderRadius: 8, fontSize: 12, color: '#888', marginBottom: 8 }}>
            <span style={{ color: '#c8a96e' }}>{pendingFile.isImage ? '📎' : '📄'}</span>
            <span>{pendingFile.name}</span>
            <span onClick={onClearFile} style={{ marginLeft: 'auto', cursor: 'pointer', color: '#555', fontSize: 14 }}>×</span>
          </div>
        )}

        {/* Input bar */}
        <div style={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: 12,
          border: `1px solid ${deepSearchActive ? '#c8a96e44' : '#2a2a2a'}`,
          borderRadius: 16,
          padding: '16px 18px',
          background: '#141414',
        }}>
          {/* Upload button */}
          <label
            style={{
              background: 'none', border: 'none',
              cursor: 'pointer', padding: 0, lineHeight: 1, flexShrink: 0,
              display: 'flex', alignItems: 'center',
            }}
            onMouseEnter={e => { e.currentTarget.querySelectorAll('path').forEach(el => el.setAttribute('stroke', '#7a9e82')); }}
            onMouseLeave={e => { e.currentTarget.querySelectorAll('path').forEach(el => el.setAttribute('stroke', '#888888')); }}
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M9 12V4" stroke="#888888" strokeWidth="1.5" strokeLinecap="round"/>
              <path d="M5.5 7L9 3.5L12.5 7" stroke="#888888" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M3 13.5v1a1 1 0 001 1h10a1 1 0 001-1v-1" stroke="#888888" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            <input type="file" accept="image/jpeg,image/png,image/gif,image/webp,application/pdf,text/plain,text/csv,application/json" style={{ display: 'none' }} onChange={e => {
              const file = e.target.files?.[0];
              if (!file) return;
              const reader = new FileReader();
              reader.onload = () => { onFileUpload?.({ base64: reader.result, name: file.name, type: file.type }); };
              reader.readAsDataURL(file);
              e.target.value = '';
            }} />
          </label>

          {/* Deep search button */}
          <button
            onClick={onDeepSearch}
            style={{
              background: 'none', border: 'none',
              cursor: 'pointer', padding: 0, lineHeight: 1, flexShrink: 0,
            }}
            onMouseEnter={e => { if (!deepSearchActive) e.currentTarget.querySelectorAll('circle, path').forEach(el => el.setAttribute('stroke', '#7a9e82')); }}
            onMouseLeave={e => { if (!deepSearchActive) e.currentTarget.querySelectorAll('circle, path').forEach(el => el.setAttribute('stroke', '#888888')); }}
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <circle cx="8" cy="8" r="5.5" stroke={deepSearchActive ? '#c8a96e' : '#888888'} strokeWidth="1.4"/>
              <path d="M12.5 12.5L16 16" stroke={deepSearchActive ? '#c8a96e' : '#888888'} strokeWidth="1.4" strokeLinecap="round"/>
              <path d="M8 5.5v5M5.5 8h5" stroke={deepSearchActive ? '#c8a96e' : '#888888'} strokeWidth="1.2" strokeLinecap="round"/>
            </svg>
          </button>

          {/* contentEditable input */}
          <div
            ref={inputRef}
            contentEditable
            suppressContentEditableWarning
            onKeyDown={handleKeyDown}
            onInput={e => setInput(e.currentTarget.innerText)}
            data-placeholder={deepSearchActive ? "What do you want to go deeper on?" : "What's on your mind"}
            style={{
              flex: 1,
              background: 'transparent',
              border: 'none',
              outline: 'none',
              color: '#d4d4d4',
              fontSize: 15,
              lineHeight: 1.6,
              fontFamily: 'Georgia, serif',
              width: '100%',
              whiteSpace: 'pre-wrap',
            }}
          />

          {/* Send / Mic */}
          {(input?.trim() || pendingFile) ? (
            <button
              onClick={() => {
                const text = inputRef.current?.innerText?.trim();
                onSend(text || '');
              }}
              style={{
                width: 30, height: 30, borderRadius: '50%',
                background: '#c8a96e', border: 'none',
                cursor: 'pointer', display: 'flex',
                alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}
            >
              <svg width="14" height="14" viewBox="0 0 18 18" fill="none">
                <path d="M9 14V4" stroke="#0a0a0a" strokeWidth="1.8" strokeLinecap="round"/>
                <path d="M4.5 8.5L9 4L13.5 8.5" stroke="#0a0a0a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          ) : (
            <button
              onClick={() => {
                const next = !voiceEnabled;
                onVoiceToggle?.(next);
                if (next) toggleVoice?.();
                else if (voiceListening) toggleVoice?.();
              }}
              style={{
                width: 30, height: 30, borderRadius: '50%',
                background: voiceListening ? '#c8a96e' : '#1e1e2a',
                border: voiceListening ? 'none' : '1px solid #333',
                cursor: 'pointer', display: 'flex',
                alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={e => {
                if (!voiceListening) {
                  e.currentTarget.style.borderColor = '#7a9e82';
                  e.currentTarget.querySelectorAll('rect, path').forEach(el => el.setAttribute('stroke', '#7a9e82'));
                }
              }}
              onMouseLeave={e => {
                if (!voiceListening) {
                  e.currentTarget.style.borderColor = '#333';
                  e.currentTarget.querySelectorAll('rect, path').forEach(el => el.setAttribute('stroke', voiceEnabled ? '#c8a96e' : '#888888'));
                }
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <rect x="9" y="2" width="6" height="12" rx="3" stroke={voiceListening ? '#07070a' : voiceEnabled ? '#c8a96e' : '#888888'} strokeWidth="1.5"/>
                <path d="M5 11a7 7 0 0014 0" stroke={voiceListening ? '#07070a' : voiceEnabled ? '#c8a96e' : '#888888'} strokeWidth="1.5" strokeLinecap="round"/>
                <path d="M12 18v4" stroke={voiceListening ? '#07070a' : voiceEnabled ? '#c8a96e' : '#888888'} strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}